<template>
  <div class="generic-data-table-wrapper">
    <VDataTableServer
      :headers="headers"
      :items="items"
      :items-length="totalItems"
      :loading="loading"
      v-model:page="page"
      v-model:items-per-page="itemsPerPage"
      @update:options="$emit('update:options', $event)"
    >
      <template v-for="(_, name) in $slots" v-slot:[name]="slotProps">
        <slot :name="name" v-bind="slotProps || {}"></slot>
      </template>

      <template #no-data>
        <div class="flex justify-center font-semibold">
          <RefetchLabel
            v-if="error"
            :fetching-error-message="t('Failed to fetch completed consultations')"
            @refetch="$emit('refetch')"
          />

          <p v-else data-testid="no-data-label">
            {{ t('No data available') }}
          </p>
        </div>
      </template>

      <template #bottom>
        <div class="flex justify-between items-center px-6 py-3">
          <VBtn
            data-testid="prev-page-btn"
            class="border-gray-200 font-semibold"
            variant="outlined"
            :disabled="page <= 1"
            @click="page--"
          >
            <template #prepend>
              <VIcon class="text-[20px]" color="gray-400">arrow_back</VIcon>
            </template>
            {{ t('Previous page') }}
          </VBtn>

          <VPagination
            data-testid="pagination"
            v-model="page"
            :length="Math.ceil(totalItems / itemsPerPage)"
            :total-visible="8"
            class="pagination"
            color="gray-500"
          />

          <VBtn
            data-testid="next-page-btn"
            class="border-gray-200 font-semibold"
            variant="outlined"
            :disabled="page >= Math.ceil(totalItems / itemsPerPage)"
            @click="page++"
          >
            {{ t('Next page') }}
            <template #append>
              <VIcon class="text-[20px]" color="gray-400">arrow_forward</VIcon>
            </template>
          </VBtn>
        </div>
      </template>
    </VDataTableServer>
  </div>
</template>

<script setup lang="ts" generic="T extends Record<string, any>">
import { VPagination, VBtn, VIcon } from 'vuetify/components'
import type { VDataTableServer } from 'vuetify/components'
import { useI18n } from 'vue-i18n'
import RefetchLabel from '@/components/common/RefetchLabel.vue'

export type DataTableHeaders = InstanceType<typeof VDataTableServer>['$props']['headers']

export interface DataTableOptions {
  page: number
  itemsPerPage: number
  sortBy: { key: string; order: 'asc' | 'desc' }[]
  groupBy: { key: string; order: 'asc' | 'desc' }[]
  search: string
}

withDefaults(
  defineProps<{
    error: string | null
    headers: DataTableHeaders
    items: T[]
    totalItems?: number
    loading?: boolean
  }>(),
  {
    totalItems: 0,
    loading: false
  }
)

defineEmits<{
  (e: 'update:options', options: any): void
  (e: 'refetch'): void
}>()

defineSlots<{
  [key: `item.${string}`]: (props: { item: T; index: number; internalItem: T }) => any
  [key: string]: (props: any) => any
}>()

const page = defineModel<number>('page', { default: 1 })
const itemsPerPage = defineModel<number>('itemsPerPage', { default: 10 })
const { t } = useI18n()
</script>

<style scoped lang="scss">
.generic-data-table-wrapper {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgb(var(--v-theme-gray-200));

  :deep(.v-data-table) {
    .v-data-table__th {
      font-weight: 600;
      color: rgb(var(--v-theme-gray-600));
      font-size: 12px;
      line-height: 18px;
      border-bottom: 1px solid rgb(var(--v-theme-gray-200));
      background: rgb(var(--v-theme-gray-50));
      white-space: nowrap;
      height: 44px;

      &.v-data-table__th--sortable {
        &:hover {
          color: rgb(var(--v-theme-gray-900));
        }
      }

      &.v-data-table__th--sorted {
        color: rgb(var(--v-theme-gray-900));
      }
    }

    .v-data-table__td {
      border-bottom: 1px solid rgb(var(--v-theme-gray-200));
      padding: 12px 24px 12px 24px;
      color: rgb(var(--v-theme-gray-500));
    }

    .v-data-table-header__sort-icon {
      font-size: 12px;
      margin-left: 4px;
    }
  }

  :deep(.pagination) {
    .v-pagination__prev,
    .v-pagination__next {
      display: none !important;
    }

    .v-pagination__item {
      margin: 0 2px;

      & > button {
        border-radius: 8px;
        min-width: unset;
        width: 40px;
        height: 40px;
        font-weight: 600;
      }
    }

    .v-pagination__item--is-active {
      & > button {
        color: rgb(var(--v-theme-gray-700)) !important;
      }
    }
  }
}
</style>
